import { Capacitor } from "@capacitor/core";
import { SPEECH_LOCALE, type AlertLang } from "@/shared/i18n/alertPhrases";

/**
 * 네이티브(안드로이드/iOS) 음성 합성 — `@capacitor-community/text-to-speech`.
 *
 * ## 왜 필요한가 (실측)
 * 안드로이드 WebView 의 `speechSynthesis.getVoices()` 는 **빈 배열**을 돌려준다.
 * 그래서 폰에서는 8개 언어가 전부 「서버 필요」로 판정되고, 모든 음성 경보가
 * 서버 edge-tts MP3 를 타야만 소리가 난다 — **데이터가 끊기면 경보가 무음**이다.
 * 밀폐공간 관제 앱에서 이건 결함이므로, 네이티브에서는 OS TTS 엔진을 먼저 쓴다.
 *
 *   네이티브: 네이티브 TTS → (실패·언어팩 없음) → 서버 edge-tts MP3
 *   웹      : Web Speech  → (로컬 음성 없음)   → 서버 edge-tts MP3   ← **변경 없음**
 *
 * ## 언어팩이 없는 언어는 억지로 넘기지 않는다
 * 안드로이드 기본 엔진(Google/삼성)은 언어 데이터가 설치돼 있지 않으면 그 언어를
 * 읽지 못한다. 그대로 `speak()` 를 부르면 **엉뚱한 음성이 읽거나 무음**이 되는데,
 * 이건 다동 매니져에서 이미 겪은 「한국어 음성이 외국어를 읽고 성공으로 기록」 결함과
 * 같은 부류다. 그래서 `getSupportedLanguages()` 로 실제 보유 언어를 먼저 확인하고,
 * 없으면 네이티브를 **건너뛰고 곧장 서버 폴백**으로 간다.
 *
 * 웹 번들 오염 방지: 플러그인은 **동적 import** 라 브라우저 경로에서는 로드되지 않고,
 * 이 모듈의 모든 함수는 `isNativePlatform() === false` 이면 no-op 이다.
 */

/** 지금 실행 중인 곳이 Capacitor 네이티브 셸인가. 웹이면 항상 false. */
export function isNativeTTS(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

type TtsModule = typeof import("@capacitor-community/text-to-speech");

let modulePromise: Promise<TtsModule> | null = null;
function loadModule(): Promise<TtsModule> {
  if (!modulePromise) {
    modulePromise = import("@capacitor-community/text-to-speech").catch((e) => {
      modulePromise = null; // 다음 발화에서 다시 시도할 수 있게 캐시를 비운다
      throw e;
    });
  }
  return modulePromise;
}

/** 예외/거부 객체에서 사람이 읽을 한 줄만 뽑는다. */
function errText(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "string") return e;
  if (e && typeof e === "object" && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return "unknown";
}

/**
 * BCP-47 태그 → 언어 base 코드(`ko-KR` → `ko`).
 * 자바 레거시 코드 보정: 인도네시아어가 구형 단말에서 `in` 으로 나올 수 있다.
 */
function normalizeBase(tag: string): string {
  const base = String(tag ?? "").trim().toLowerCase().split(/[-_]/)[0] ?? "";
  if (base === "in") return "id";
  if (base === "iw") return "he";
  if (base === "ji") return "yi";
  return base;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

let baseCache: Set<string> | null = null;
let probing: Promise<Set<string>> | null = null;
/** 마지막 「빈 결과」 조회 시각(ms). 재시도 폭주로 경보가 늦어지지 않게 쿨다운을 둔다. */
let emptyProbeAt = 0;
const EMPTY_PROBE_COOLDOWN_MS = 60_000;

/**
 * 이 단말의 TTS 엔진이 **실제로 읽을 수 있는 언어**의 base 코드 집합.
 * 웹에서는 항상 빈 집합. 절대 throw 하지 않는다.
 *
 * 안드로이드 엔진은 초기화가 끝나기 전에 물으면 거부하거나 빈 목록을 주므로,
 * 결과가 비면 짧게 재시도하고 **비어 있는 결과는 캐시하지 않는다**(나중에 다시 확인).
 */
export async function nativeSupportedBases(): Promise<Set<string>> {
  if (!isNativeTTS()) return new Set();
  if (baseCache) return baseCache;
  if (probing) return probing;
  // 직전 조회가 빈 결과였으면(엔진 없음/언어팩 0개) 잠시 그대로 둔다 — 경보마다
  // 몇 초씩 재시도하며 서버 폴백을 늦추는 것이 더 위험하다.
  if (emptyProbeAt && Date.now() - emptyProbeAt < EMPTY_PROBE_COOLDOWN_MS) {
    return new Set();
  }

  probing = (async () => {
    const { TextToSpeech } = await loadModule();
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const res = await TextToSpeech.getSupportedLanguages();
        const set = new Set(
          (res?.languages ?? []).map(normalizeBase).filter((b) => b.length > 0),
        );
        if (set.size) {
          baseCache = set;
          return set;
        }
      } catch {
        /* 엔진 초기화 대기 — 아래에서 재시도 */
      }
      await sleep(300 * (attempt + 1));
    }
    emptyProbeAt = Date.now();
    return new Set<string>();
  })()
    .catch(() => new Set<string>())
    .finally(() => {
      probing = null;
    });

  return probing;
}

/** 캐시를 비워 다음 조회에서 엔진에 다시 묻게 한다(언어팩 설치 후 등). */
export function resetNativeVoiceCache(): void {
  baseCache = null;
  emptyProbeAt = 0;
}

export type NativeSpeakResult =
  /** 네이티브 엔진이 끝까지 읽었다. */
  | { status: "ok" }
  /** 이 단말에 해당 언어 데이터가 없다(또는 웹) → 조용히 서버 폴백으로. */
  | { status: "unsupported" }
  /** 읽으려다 실패했다 → 기록하고 서버 폴백으로. */
  | { status: "error"; reason: string };

/**
 * `text` 를 `lang` 으로 네이티브 발화하고 **끝날 때까지 기다린다**.
 * 호출부의 FIFO 큐가 겹치지 않도록 반드시 await 할 것. 절대 throw 하지 않는다.
 */
export async function nativeSpeak(text: string, lang: AlertLang): Promise<NativeSpeakResult> {
  if (!isNativeTTS()) return { status: "unsupported" };

  // 언어팩 보유 여부 먼저 — 없으면 네이티브를 건너뛴다(엉뚱한 음성/무음 방지).
  let bases: Set<string>;
  try {
    bases = await nativeSupportedBases();
  } catch {
    bases = new Set();
  }
  if (!bases.has(lang)) return { status: "unsupported" };

  try {
    const { TextToSpeech, QueueStrategy } = await loadModule();
    const spoken = TextToSpeech.speak({
      text,
      lang: SPEECH_LOCALE[lang],
      rate: 1,
      pitch: 1,
      volume: 1,
      // iOS: 화면이 꺼져 있어도 경보가 나가야 한다.
      category: "playback",
      // 호출부 워치독이 먼저 만료돼 다음 항목이 들어와도 잘라먹지 않고 뒤에 붙인다.
      queueStrategy: QueueStrategy.Add,
    });

    // ⛔ 반드시 settle 시킨다. 플러그인의 `stop()` 은 콜백 맵을 비워 버려서
    //    (android TextToSpeech.java `requests.clear()`) 그 시점에 읽고 있던 발화의
    //    Promise 가 **영영 resolve/reject 되지 않는다**. 그대로 두면 호출부가 매달려
    //    성공도 실패도 기록하지 못하고 서버 폴백도 못 타는 「완전 무음 + 무기록」이 된다.
    const budget = Math.min(60000, Math.max(8000, text.length * 260 + 4000));
    const timedOut = Symbol("tts-timeout");
    const r = await Promise.race([
      spoken.then(() => "ok" as const),
      new Promise<typeof timedOut>((res) => setTimeout(() => res(timedOut), budget)),
    ]);
    if (r === timedOut) return { status: "error", reason: `native_timeout_${budget}ms` };
    return { status: "ok" };
  } catch (e) {
    return { status: "error", reason: errText(e) };
  }
}

/** 진행 중인 네이티브 발화를 중단한다(음성 끄기·방송 정지). 웹에서는 no-op. */
export function nativeStopSpeaking(): void {
  if (!isNativeTTS()) return;
  void loadModule()
    .then(({ TextToSpeech }) => TextToSpeech.stop())
    .catch(() => {
      /* 아직 초기화 전이거나 재생 중이 아님 — 무시 */
    });
}
